import { useCallback, useMemo } from 'react'
import PageWrapper from 'hoc/PageWrapper'
import { Delete } from '@mui/icons-material'
import {
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  styled,
  TableCell,
  tableCellClasses,
  TableBody,
  IconButton,
} from '@mui/material'
import { ETenderType } from 'types/tender'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { tenderTypeToTitle } from 'utils/tender'
import { useTranslation } from 'react-i18next'
import { useTendersQuery } from 'services/tender'
import { Loading } from 'components/Loading'
import { useDeleteLotMutation } from 'services/lots'
import 'styles/TendersPage.scss'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import { TablePagination } from 'components/TablePagination'
import { useQueryState } from 'react-router-use-location-state'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

export const Tenders = () => {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [page, setPage] = useQueryState('page', 0)

  const rowsPerPage = 10

  const args = {
    page,
    size: rowsPerPage,
  } as const

  const [deleteLot] = useDeleteLotMutation()

  const { data: tenders, isLoading } = useTendersQuery(args, {
    refetchOnMountOrArgChange: true,
  })

  const type = params.get('type') as ETenderType

  const handleNew = () => {
    navigate(`/portal/tenders/create?type=${type}`)
  }

  const handleDelete = useCallback(
    (id: string) => {
      deleteLot({ id: id })
    },
    [deleteLot]
  )

  const tendersToShow = useMemo(() => {
    return tenders?.items.filter((t) => t.method === type)
  }, [tenders, type])

  const toEditLotPage = (id: string) => {
    navigate(`/portal/tenders/${id}/edit?type=${type}`)
  }

  if (!type) {
    navigate('/')
  }

  if (isLoading) {
    return <Loading />
  }

  if (tenders === undefined) {
    return <></>
  }

  return (
    <PageWrapper>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Typography fontWeight={700}>{t(tenderTypeToTitle(type))}</Typography>
          <Button
            onClick={handleNew}
            size="small"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            sx={{ ml: 1.5 }}
          >
            {t('createNew')}
          </Button>
        </Box>
        <Button size="small" variant="outlined" onClick={() => navigate('/portal')}>
          {t('back')}
        </Button>
      </Box>

      <Box mt={4}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>{t('tender-name')}</StyledTableCell>
                <StyledTableCell>{t('status')}</StyledTableCell>
                <StyledTableCell>{t('startsAt')}</StyledTableCell>
                <StyledTableCell>{t('endDateOfCompetition')}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tendersToShow?.map(
                ({ id, name, status, startTime, envelopeOpeningTime }) => (
                  <TableRow className={`tableRow`} onClick={() => toEditLotPage(id)}>
                    <TableCell>{name}</TableCell>
                    <TableCell>{t(`tender-status-${status}`)}</TableCell>
                    <TableCell>
                      {startTime && formatTimeStampToDate(startTime)}
                    </TableCell>
                    <TableCell>
                      {envelopeOpeningTime &&
                        formatTimeStampToDate(envelopeOpeningTime)}
                    </TableCell>
                    {/* <TableCell>
                      <IconButton onClick={() => handleDelete(id)}>
                        <Delete color="error" />
                      </IconButton>
                    </TableCell> */}
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {tenders.info.totalRows !== 0 && (
          <TablePagination
            total={tenders.info.totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}
      </Box>
    </PageWrapper>
  )
}
